import { Form, Formik, FormikActions, FormikProps } from "formik";
import React from "react";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import * as yup from "yup";
import InputField from "../../../components/Form/InputField";
import RadioGroup from "../../../components/Form/RadioGroup";
import SubmitButton from "../../../components/Form/SubmitButton";
import processSubmitErrors from "../../../helpers/processSubmitErrors";
import { emailRegex, phonePlus } from "../../../helpers/valid";
import reportsShareRequestPost, {
  ReportsRequestParams
} from "../../../services/reportsShareRequestPost";
import styles from "./RequestReportForm.module.scss";

interface FormValues {
  sendMethod: "email" | "phone";
  name: string;
  email: string;
  phone: string;
}

const RequestReportForm = () => {
  const initialValues: FormValues = {
    sendMethod: "email",
    name: "",
    email: "",
    phone: "",
  };

  const validationSchema = yup.object().shape({
    sendMethod: yup
      .string()
      .required("Elija una opción"),
    name: yup
      .string()
      .required("Se requiere el nombre del destinatario"),
    email: yup
      .string()
      .when("sendMethod", {
        is: value => value === "email",
        then: yup
          .string()
          .required("El correo es requerido")
          .email("El correo no es válido")
          .matches(emailRegex, "El correo no es válido")
      }),
    phone: yup
      .string()
      .when("sendMethod", {
        is: value => value === "phone",
        then: yup
          .string()
          .required("El teléfono es requerido")
          .min(10, "El número de móvil no es válido")
          .matches(phonePlus, "El número de móvil no es válido")
      }),
  });

  const methods = [
    { name: "Correo", value: "email" },
    { name: "Teléfono móvil", value: "phone" },
  ];

  const handleSubmit = async (
    values: FormValues,
    {
      setErrors,
      setStatus,
      setSubmitting,
      resetForm
    }: FormikActions<FormValues>
  ) => {
    try {
      const sendMethod = values.sendMethod;
      const data: ReportsRequestParams = {
        sendMethod,
        name: values.name,
        email: sendMethod === "email" ? values.email : undefined,
        phone: sendMethod === "phone" ? values.phone : undefined,
      };
      setStatus({ successMessage: "" });
      await reportsShareRequestPost(data);
      resetForm();
      setStatus({ successMessage: "Tu solicitud ha sido enviada" });
    } catch (error) {
      processSubmitErrors(error, values, setErrors, setStatus);
    }
    setSubmitting(false);
  }

  return (
    <>
      <p className={styles.title}>
        Solicitar el reporte crediticio a un conocido
      </p>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        render={({ values, setFieldValue }: FormikProps<FormValues>) => (
          <Form className={styles.form}>
            <RadioGroup
              name="sendMethod"
              label="¿Cómo deseas solicitar el reporte?"
              options={methods}
              className={styles.field}
            />
            <div className={styles.grid}>
              <InputField
                name="name"
                label="Nombre"
                className={styles.field}
              />
              {values.sendMethod === "email"
                ? (
                  <InputField
                    name="email"
                    label="Correo"
                    className={styles.field}
                  />
                )
                : (
                  <div className={styles.field}>
                    <label htmlFor="phone">Teléfono móvil</label>
                    <PhoneInput
                      country={'co'}
                      value={values.phone}
                      onChange={phone => setFieldValue('phone', phone)}
                      enableSearch
                      inputProps={{
                        name: 'phone',
                        required: true,
                        id: 'phone'
                      }}
                      inputStyle={{
                        width: '100%',
                        height: '40px'
                      }}
                    />
                  </div>
                )
              }
            </div>
            <div className={styles.container}>
              <SubmitButton
                id="request-report"
                text="Solicitar reporte"
                className={styles.button}
              />
            </div>
          </Form>
        )}
      />
    </>
  );
};

export default RequestReportForm;
